<template>
    <div class="units">
        <!-- Toolbar - Global for both table and chart -->
        <table-toolbar
            :show-title="true"
            title="Units"
            :show-refresh="false"
        >
            <template #action-buttons>
                <themed-button
                    v-if="showEditActions"
                    @click="showCreateUnit"
                    variant="default"
                    size="sm"
                >
                    <theme-icon icon="add" type="icons" class="units__add-icon" />
                    New Unit
                </themed-button>
            </template>
        </table-toolbar>

        <!-- Content: 50-50 Split Layout -->
        <div class="units__content" :class="{ 'units__content--single-column': !showChart }">
            <!-- Units Table (Left Side) -->
            <div class="units__table-container">
                <themed-table
                    :data="sortedUnits"
                    :columns="tableColumns"
                    :hoverable="true"
                    :pagination="true"
                    :per-page-options="[10, 25, 50]"
                    aria-label="Units table"
                    @action="handleAction"
                >
                    <template #column-status="{ row }">
                        <span class="badge" :class="getStatusBadgeClass(row.status)">
                            {{ row.status }}
                        </span>
                    </template>
                </themed-table>
            </div>

            <!-- Chart (Right Side) -->
            <div v-if="showChart" class="units__chart-container">
                <h3 class="units__chart-title">Square Footage by Floor</h3>
                <div class="units__chart-wrapper">
                    <canvas ref="chartCanvas"></canvas>
                </div>
            </div>
        </div>

        <!-- Manage Units Modal -->
        <themed-modal 
            name="manage-units" 
            title="Manage Units"
            size="xl"
            custom-class="manage-units-modal"
            @opened="handleModalOpened"
            @closed="handleModalClosed"
        >
            <manage-units-v1 
                ref="manageUnits"
                @loading-change="loading = $event"
                @unit-saved="handleUnitSaved"
            ></manage-units-v1>
            
            <template #footer>
                <div class="modal-actions">
                    <themed-button variant="secondary" @click="closeModal">
                        Close
                    </themed-button>
                    <themed-button
                        v-if="units.selectedUnitId"
                        variant="default"
                        :disabled="loading"
                        @click="saveUnit"
                    >
                        {{ loading ? 'Saving...' : 'Save Unit' }}
                    </themed-button>
                </div>
            </template>
        </themed-modal>

    </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedModal from '../ThemedModal.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';
import ManageUnitsV1 from './ManageUnits.vue';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    Title,
    Tooltip,
    Legend
);

export default {
    name: 'ThemedUnits',
    
    components: {
        ThemedTable,
        ThemedButton,
        ThemedModal,
        ThemeIcon,
        TableToolbar,
        ManageUnitsV1
    },
    
    props: {
        showEditActions: {
            type: Boolean,
            default: false
        },
        showChart: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            loading: false,
            isDarkMode: document.documentElement.classList.contains('dark'),
            themeObserver: null,
            resizeObserver: null,
            chart: null
        };
    },

    computed: {
        ...mapState(['buildings', 'units']),

        sortedUnits() {
            const units = this.buildings.selectedBuilding?.units || [];
            return _.sortBy(units, [(unit) => parseInt(unit.floor)]);
        },

        tableColumns() {
            const cols = [
                { 
                    field: 'floor', 
                    label: 'Floor', 
                    sortable: true,
                    width: '100px',
                    align: 'center',
                    formatter: (value) => value || '-'
                },
                { 
                    field: 'suite', 
                    label: 'Suite', 
                    sortable: true,
                    width: '120px',
                    formatter: (value) => value || '-'
                },
                { 
                    field: 'size', 
                    label: 'Size', 
                    sortable: true,
                    width: '120px',
                    align: 'right',
                    formatter: (value) => value ? this.formatNumber(value) : '-'
                },
                { 
                    field: 'status', 
                    label: 'Status', 
                    sortable: true,
                    width: '120px',
                    align: 'center'
                },
                { 
                    field: 'rent_cost', 
                    label: 'Ask Price', 
                    sortable: true,
                    width: '140px',
                    align: 'right',
                    formatter: (value) => this.formatCurrency(value, 2)
                },
                { 
                    field: 'updated_at', 
                    label: 'Last Update', 
                    sortable: true,
                    width: '140px',
                    formatter: (value) => value ? this.$options.filters.date(value) : '-'
                }
            ];

            if (this.showEditActions) {
                cols.push({
                    field: 'actions',
                    label: '',
                    type: 'actions',
                    width: '100px',
                    sortable: false,
                    showInTable: true,
                    buttons: [
                        {
                            name: 'edit',
                            label: 'Edit',
                            icon: 'edit',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => true
                        },
                        {
                            name: 'delete',
                            label: 'Delete',
                            icon: 'trash',
                            variant: 'ghost',
                            size: 'sm',
                            iconOnly: true,
                            visible: () => true
                        }
                    ]
                });
            }

            return cols;
        },

        buildingFloors() {
            const stackData = this.buildings.selectedBuilding?.stack || {};
            const numericFloors = [];
            const nonNumericFloors = [];

            _.keys(stackData).forEach(key => {
                if (parseInt(key)) {
                    numericFloors.push(parseInt(key));
                } else {
                    nonNumericFloors.push(key);
                }
            });

            numericFloors.sort((a, b) => b - a);
            nonNumericFloors.sort().reverse();

            return [...numericFloors, ...nonNumericFloors];
        },

        chartData() {
            const stackData = this.buildings.selectedBuilding?.stack || {};
            const floors = this.buildingFloors;

            const availableData = floors.map(floor => {
                return stackData[floor]?.Available || 0;
            });

            const occupiedData = floors.map(floor => {
                return stackData[floor]?.Occupied || 0;
            });

            return {
                labels: floors.map(f => `Floor ${f}`),
                datasets: [
                    {
                        label: 'Available',
                        data: availableData,
                        backgroundColor: '#10b981',
                        borderColor: '#10b981',
                        borderWidth: 0,
                        barThickness: 24,
                        borderRadius: 4
                    },
                    {
                        label: 'Occupied',
                        data: occupiedData,
                        backgroundColor: this.isDarkMode ? '#475569' : '#9ca3af',
                        borderColor: this.isDarkMode ? '#475569' : '#9ca3af',
                        borderWidth: 0,
                        barThickness: 24,
                        borderRadius: 4
                    }
                ]
            };
        },

        chartOptions() {
            const textColor = this.isDarkMode ? '#94a3b8' : '#64748b';
            const gridColor = this.isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.15)';
            const tooltipBg = this.isDarkMode ? '#1e293b' : '#ffffff';
            const tooltipBorder = this.isDarkMode ? '#334155' : '#e5e7eb';

            return {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'index',
                    intersect: false
                },
                plugins: {
                    legend: {
                        position: 'bottom',
                        align: 'center',
                        labels: {
                            color: textColor,
                            padding: 16,
                            usePointStyle: true,
                            pointStyle: 'circle',
                            font: {
                                size: 12,
                                family: 'Inter, system-ui, sans-serif'
                            },
                            boxWidth: 8,
                            boxHeight: 8
                        }
                    },
                    tooltip: {
                        backgroundColor: tooltipBg,
                        titleColor: textColor,
                        bodyColor: textColor,
                        borderColor: tooltipBorder,
                        borderWidth: 1,
                        padding: 12,
                        displayColors: true,
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || '';
                                const value = context.parsed.x;
                                return `${label}: ${value.toLocaleString()} sq ft`;
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        stacked: true,
                        grid: {
                            color: gridColor,
                            drawBorder: false,
                            lineWidth: 1
                        },
                        ticks: {
                            color: textColor,
                            font: {
                                size: 11,
                                family: 'Inter, system-ui, sans-serif'
                            },
                            padding: 8,
                            callback: (value) => {
                                if (value >= 1000) {
                                    return (value / 1000).toFixed(0) + 'k';
                                }
                                return value;
                            }
                        },
                        title: {
                            display: false
                        }
                    },
                    y: {
                        stacked: true,
                        grid: {
                            display: false,
                            drawBorder: false
                        },
                        ticks: {
                            color: textColor,
                            font: {
                                size: 12,
                                family: 'Inter, system-ui, sans-serif'
                            },
                            padding: 8
                        }
                    }
                }
            };
        }
    },

    mounted() {
        if (this.showChart) {
            this.createChart();
            this.setupThemeObserver();
            this.setupResizeObserver();
        }
    },

    beforeDestroy() {
        if (this.themeObserver) {
            this.themeObserver.disconnect();
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.chart) {
            this.chart.destroy();
        }
    },

    watch: {
        'buildings.selectedBuilding.stack': {
            handler() {
                if (this.showChart) {
                    this.$nextTick(() => {
                        this.updateChart();
                    });
                }
            },
            deep: true
        }
    },

    methods: {
        formatNumber(value) {
            if (value === null || value === undefined) return '-';
            return new Intl.NumberFormat().format(value);
        },

        getStatusBadgeClass(status) {
            if (!status) return 'badge--yellow';
            
            const statusLower = status.toLowerCase();
            
            if (statusLower === 'available') {
                return 'badge--green';
            } else if (statusLower === 'occupied') {
                return 'badge--blue';
            } else if (statusLower === 'sublease') {
                return 'badge--yellow';
            } else if (statusLower === 'vacant') {
                return 'badge--red';
            }
            
            return 'badge--yellow';
        },

        showCreateUnit() {
            const existingNewUnit = _.find(this.units.all, ['id', 'new']);
            
            if (!existingNewUnit) {
                this.units.all.push({
                    id: 'new',
                    building_id: this.buildings.selectedBuilding.id,
                    expenses: [],
                    status: null,
                    is_renewal_space: null,
                    size: null,
                    divisible: null,
                    floor: null,
                    suite: null,
                    ti_allowance: null,
                    comment: null
                });
            }

            this.units.selectedUnitId = 'new';
            this.$modal.show('manage-units');
        },

        showEditUnit(unitId) {
            const unitInStore = _.find(this.units.all, ['id', unitId]);
            if (!unitInStore) {
                const unitFromBuilding = _.find(this.buildings.selectedBuilding.units, ['id', unitId]);
                if (unitFromBuilding) {
                    this.units.all.push(unitFromBuilding);
                }
            }
            
            this.units.selectedUnitId = unitId;
            this.$modal.show('manage-units');
        },

        removeUnit(unitId) {
            const unit = _.find(this.units.all, ['id', unitId]);

            if (!unit) {
                return;
            }

            const stackSize = this.buildings.selectedBuilding.stack?.[unit.floor]?.[unit.status];

            this.buildings.selectedBuilding.units = _.remove(
                this.buildings.selectedBuilding.units, 
                (unit) => unit.id != unitId
            );
            this.units.all = _.remove(this.units.all, (unit) => unit.id != unitId);

            if (stackSize !== undefined) {
                this.buildings.selectedBuilding.stack[unit.floor][unit.status] = stackSize - unit.size;
            }
        },

        confirmDeleteUnit(unitId) {
            this.$swal({
                title: 'Delete this unit?',
                text: "ALL data will be lost, including any lease information attached to this unit",
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Delete it',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.deleteUnit(unitId);
                }
            });
        },

        deleteUnit(unitId) {
            if (unitId == 'new') {
                this.removeUnit(unitId);
                return;
            }

            axios.delete('/units/' + unitId)
                .then(response => {
                    this.removeUnit(unitId);

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
                })
                .catch(error => {
                    console.error(error);
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                });
        },

        handleAction({ action, row }) {
            if (action === 'edit') {
                this.showEditUnit(row.id);
            } else if (action === 'delete') {
                this.confirmDeleteUnit(row.id);
            }
        },

        closeModal() {
            if (this.units.selectedUnitId == 'new') {
                this.removeUnit('new');
            }
            this.$modal.hide('manage-units');
        },

        saveUnit() {
            if (this.$refs.manageUnits) {
                this.$refs.manageUnits.saveUnit();
            }
        },

        handleUnitSaved() {
            this.$modal.hide('manage-units');
        },

        handleModalOpened() {
            this.loading = false;
        },

        handleModalClosed() {
            this.loading = false;
        },

        createChart() {
            if (!this.$refs.chartCanvas) {
                return;
            }

            const ctx = this.$refs.chartCanvas.getContext('2d');
            
            if (this.chart) {
                this.chart.destroy();
            }

            this.chart = new ChartJS(ctx, {
                type: 'bar',
                data: this.chartData,
                options: this.chartOptions
            });
        },

        updateChart() {
            if (!this.chart) {
                this.createChart();
                return;
            }

            this.chart.data = this.chartData;
            this.chart.options = this.chartOptions;
            this.chart.update();
        },

        setupThemeObserver() {
            this.themeObserver = new MutationObserver(() => {
                this.isDarkMode = document.documentElement.classList.contains('dark');
                if (this.chart) {
                    this.updateChart();
                }
            });
            
            this.themeObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['class']
            });
        },

        setupResizeObserver() {
            if (window.ResizeObserver) {
                this.resizeObserver = new ResizeObserver(() => {
                    if (this.chart) {
                        this.chart.resize();
                    }
                });
                
                if (this.$el) {
                    this.resizeObserver.observe(this.$el);
                }
            }
        }
    }
}
</script>
